import type {
  Accordion as AccordionType,
  Content as ContentType,
} from '@snapchat/mw-contentful-schema';
import {
  AccordionItem as AccordionItemSDS,
  Accordion as AccordionSDS,
} from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useContext } from 'react';
import type { ContentfulSysProps } from 'src/types/contentful';

import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { logUserEvent } from '../../helpers/logging';
import type { ContentfulIdVariable } from '../../hooks/useContentfulQuery';
import { useContentfulQuery } from '../../hooks/useContentfulQuery';
import { UserAction } from '../../types/events';
import type { RichText } from '../../types/RichText';
import { isContentfulSysProps } from '../../utils/contentful';
import { getContentfulInspectorProps } from '../../utils/contentful/getContentfulInspectorProps';
import { renderRichTextMarkingsOnly } from '../../utils/renderText/renderRichText';
import { Content } from '../Content';
import { queries } from './query';
import type { AccordionDataHandlerProps } from './types';

export const Accordion: FC<ContentfulSysProps> = (props: ContentfulSysProps) => {
  const id = isContentfulSysProps(props) ? props.sys.id : undefined;
  const elementLocation = useContext(ElementLocation).elementLocation;

  const { data } = useContentfulQuery<AccordionDataHandlerProps, ContentfulIdVariable>(
    queries.all,
    {
      skip: !id,
      variables: { id },
    }
  );

  if (!data) return null;

  const { titleDataset } = getContentfulInspectorProps<AccordionType>({
    entryId: id!,
    fieldIds: ['title'],
  });

  const accordionItems = data?.accordion.itemsCollection.items;

  return (
    <AccordionSDS
      data-testid="accordion"
      multipleOpen={data?.accordion.multipleOpen}
      title={data?.accordion.title}
      titleDataset={titleDataset}
    >
      {accordionItems.map(item => {
        const { titleDataset } = getContentfulInspectorProps<ContentType>({
          entryId: item.sys.id,
          fieldIds: ['title'],
        });

        const plainTitle = item.title ? item.title : item.sys.id;

        return (
          <AccordionItemSDS
            id={item.sys.id}
            key={item.sys.id}
            title={renderRichTextMarkingsOnly(item.title)}
            titleDataset={titleDataset}
            onToggle={(isExpanding: boolean) => {
              logUserEvent({
                eventCategory: 'Accordion',
                eventAction: isExpanding ? UserAction.Open : UserAction.Close,
                eventLabel: null,
                context: { elementText: plainTitle, elementLocation },
              });
            }}
          >
            <Content
              body={item.body as RichText}
              callsToActionCollection={item.callsToActionCollection ?? { items: [] }}
              hasLinks={false}
              sys={item.sys}
            />
          </AccordionItemSDS>
        );
      })}
    </AccordionSDS>
  );
};
